import { jwtDecode } from 'jwt-decode';
import { instance, instanceAuth, instanceTimekeeper } from './instances';
import { type APIResponse, type LoginResponse, type Permission, type AcesssTokenPayload } from './types/sign.d';
import type { AxiosResponse } from 'axios';

const logout = (setAuthenticated: (authenticated: boolean) => void) => {
    localStorage.clear();
    sessionStorage.clear();
    setAuthenticated(false);
};

const getConfigSystem = async (bearer: string) => {
    let config_system: {
        key: string;
        value: string;
    }[] = [];

    await instanceTimekeeper
        .get('/config_system', {
            headers: {
                Authorization: `Bearer ${bearer}`,
            },
        })
        .then((response) => {
            localStorage.setItem('config_system', JSON.stringify(response.data.data));

            config_system = response.data.data;
        })
        .catch((error) => {
            console.error(error.message);
        })
        .finally(() => { });

    return {
        config_system,
    };
};

const getPermissionsIdListFromHex = (permissionsHex: string) => {
    let numero = BigInt(`0x${permissionsHex}`);
    let binary = numero.toString(2);
    let permissions = [...binary].map((bit, i) => (bit === '1' ? i + 1 : null)).filter(Number);
    return permissions;
};

const getPermissionsListFromId = (permissionsIds: (number | null)[], permissionsNameList: any) => {
    let permissions = permissionsIds.map((id) => permissionsNameList.find((p: any) => p.permission_id === id)?.name).filter((name) => name !== undefined);
    return permissions;
};

const getAllPermissions = async (): Promise<Permission[]> => {
    const storedPermissions = localStorage.getItem('all_permissions');
    const permissionsDate = new Date(localStorage.getItem('all_permissions_expire') || '1970-01-01T00:00:00Z');

    if (storedPermissions !== null && storedPermissions !== 'undefined' && permissionsDate >= new Date()) {
        try {
            return JSON.parse(storedPermissions) as Permission[];
        } catch {
            localStorage.removeItem('all_permissions');
        }
    }

    try {
        const response: AxiosResponse<APIResponse<Permission[]>> = await instanceAuth.get('/v1/permission');
        const permissions = response.data.data;
        const futureTime = new Date(Date.now() + 30 * 60 * 1000);

        localStorage.setItem('all_permissions', JSON.stringify(permissions));
        localStorage.setItem('all_permissions_expire', futureTime.toISOString());
        sessionStorage.setItem('permissions_loaded', 'true');

        return permissions;
    } catch (error) {
        console.error('Error fetching permissions:', error);
        return [];
    }
};

const login = async (
    user: string,
    password: string,
    setLoading: (loading: boolean) => void,
    setAuthenticated: (authenticated: boolean) => void,
    navigate: (to: string) => void,
): Promise<{
    accessToken: string;
    permissionCatalog: Permission[];
} | null> => {
    setLoading(true);

    let formData = new FormData();

    formData.append('grant_type', 'password');
    formData.append('client_id', import.meta.env.VITE_CLIENT_ID);
    formData.append('scope', 'timekeeper scheduled employee auth mdm_psc custom_reports');
    formData.append('username', user);
    formData.append('password', password);

    try {
        const response: AxiosResponse<LoginResponse> = await instance.post('/v1/login', formData);
        localStorage.setItem('responde.data', JSON.stringify(response.data));
        localStorage.setItem('access_token', response.data.access_token);

        let decodedAccessToken: AcesssTokenPayload = jwtDecode(response.data.access_token);

        localStorage.setItem('assigned_tags', decodedAccessToken.tags.toString());

        let permissionsIds = getPermissionsIdListFromHex(decodedAccessToken.permissions_hex);

        const permissionsNameList = await getAllPermissions();

        let permissions = getPermissionsListFromId(permissionsIds, permissionsNameList);

        localStorage.setItem('permissions', JSON.stringify(permissions));
        localStorage.setItem('token_type', response.data.token_type);
        localStorage.setItem('expires_in', response.data.expires_in.toString());
        if (response.data.refresh_token) {
            localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        if (response.data.refresh_expires_in) {
            localStorage.setItem('refresh_expires_in', response.data.refresh_expires_in.toString());
        }

        localStorage.setItem('authenticated', 'true');
        localStorage.setItem('user_id', decodedAccessToken.user_id.toString());

        const { config_system } = await getConfigSystem(response.data.access_token);

        setAuthenticated(true);

        const redirectPath: string = config_system.find((config) => config.key === 'login_redirect')?.value || '';

        // redirect to
        localStorage.setItem('history_path', JSON.stringify([`/${redirectPath}`]));
        navigate(`/${redirectPath}`);

        return {
            accessToken: response.data.access_token,
            permissionCatalog: permissionsNameList,
        };
    } catch (error) {
        console.error(error);
        return null;
    } finally {
        setLoading(false);
    }
};

const syncSessionFromToken = (accessToken: string) => {
    const decoded: AcesssTokenPayload = jwtDecode(accessToken);
    const permissionsNameList = JSON.parse(
        localStorage.getItem("all_permissions") || "[]",
    );
    const permissions = getPermissionsListFromId(
        getPermissionsIdListFromHex(decoded.permissions_hex),
        permissionsNameList,
    );

    localStorage.setItem("permissions", JSON.stringify(permissions));
    localStorage.setItem("assigned_tags", decoded.tags?.toString() ?? "");
    localStorage.setItem("user_id", String(decoded.user_id));
};

const signServices = {
    logout,
    getConfigSystem,
    getAllPermissions,
    login,
    syncSessionFromToken,
};

export { signServices };
